import { Avatar, AvatarGroup, Skeleton, Tooltip, Typography, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import moment from 'moment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { ACTIVE_PROJECT_TAB } from '../../../constants/common';
import { GET_CURRENT_VERSION_DATA, REDO_VERSION, RESTORE_PREVIOUS_VERSION, UNDO_REDO_VERSION, UNDO_VERSION } from '../../../apis/version';
import { GET_PROJECT, PROJECT_WARNINGS, GET_SHARE_PROJECT } from '../../../apis/project';
import { GET_WORKSPACE_PROFILES } from '../../../apis/workspace';
import { PROJECTS } from '../../../constants/routes';
import { generateInitials } from '../../../components/Avatar';
import BreadCrumb from '../../../components/BreadCrumb';
import Button from '../../../components/atoms/button';
import EditMenu from '../../../components/EditMenu';
import EpicList from '../../../components/EpicList';
import Error404 from '../../Error/Error404';
import MemberList from '../../../components/MemberList';
import ProjectContext from '../../../context/ProjectContext';
import ProjectTitle from '../../../components/Heading/ProjectTitle';
import ProjectWarningContext from '../../../context/ProjectWarningContext';
import RedoButton from '../../../images/icons/redoEdit.svg';
import RiskBar from '../../../components/RiskBar';
import ShareProjectButton from '../../../components/Button/ShareProjectButton';
import SprintDialog from '../../../components/SprintDialog';
import TabPanel from '../../../components/TabMenu';
import TimeLine from '../../../components/Timeline/Timeline';
import UndoButton from '../../../images/icons/undoEdit.svg';
import useAuth from '../../../context/AuthContext';
import useLoader from '../../../context/LoaderContext';
import useProjectVersionHistory from '../../../context/VersionHistoryContext';
import useWorkspace from '../../../context/WorkspaceContext';
import WorkspaceOptions from '../../../components/molecules/workspace-options';

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    color: '#FFFFFF',
    background: '#344054',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '12px',
    lineHeight: '18px',
    borderRadius: '6px',
    marginTop: '9px !important',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#344054',
  },
}));

const a11yProps = (index) => ({ id: `simple-tab-${index}`, 'aria-controls': `simple-tabpanel-${index}` });

const ProjectDetail = () => {
  // init
  const { dispatch, usersOfProjects, projectId } = useContext(ProjectContext);
  const { userPermissions, showTeamTour, workspace, workspaceProfile } = useWorkspace();
  const { authUser } = useAuth();
  const { isLoading, setIsLoading } = useLoader();
  const { dispatch: dispatchRiskBar, showRiskBar } = useContext(ProjectWarningContext);
  const navigate = useNavigate();
  const params = useParams();
  const {
    showVersionHistoryBar,
    readOnlyMode,
    selectedListItem,
    undoEdits,
    redoEdits,
    setUndoEdits,
    setRedoEdits,
    setIsCurrentVersion,
    setSelectedListItem,
    setShowVersionHistoryBar,
    setReadOnlyMode,
    setUndo,
    setRedo,
  } = useProjectVersionHistory();

  // states
  const [workspaceProfiles, setWorkspaceProfiles] = useState([]);
  const [project, setProject] = useState({});
  const [value, setValue] = useState(showTeamTour ? 2 : 0);
  const [openDialog, setOpenDialog] = useState(false);
  const [projectNotExists, setProjectNotExists] = useState(false);

  // mutations/queries
  const [getWorkspaceProfiles] = useLazyQuery(GET_WORKSPACE_PROFILES, { fetchPolicy: 'network-only' });
  const [getProjectDetail] = useLazyQuery(GET_PROJECT, { fetchPolicy: 'network-only', variables: { input: { id: params.id } } });
  const [restorePreviousVersion] = useLazyQuery(RESTORE_PREVIOUS_VERSION);
  const [undoVersionMutation] = useLazyQuery(UNDO_VERSION, { fetchPolicy: 'network-only' });
  const [redoVersionMutation] = useLazyQuery(REDO_VERSION, { fetchPolicy: 'network-only' });
  const [undoRedoEdits] = useLazyQuery(UNDO_REDO_VERSION, { fetchPolicy: 'network-only', variables: { input: { projectId: parseInt(params.id, 10) } } });
  const [getCurrentVersionData] = useLazyQuery(GET_CURRENT_VERSION_DATA, { fetchPolicy: 'network-only', variables: { input: { projectId: parseInt(params.id, 10), showVersion: false } } });
  const [getProjectWarnings] = useLazyQuery(PROJECT_WARNINGS, { fetchPolicy: 'no-cache', variables: { projectId: parseInt(params.id, 10) } });
  const [getSharedProject] = useLazyQuery(GET_SHARE_PROJECT, { fetchPolicy: 'no-cache' });

  const isHidden = showVersionHistoryBar && readOnlyMode && selectedListItem !== null && selectedListItem !== undefined;

  const onTabChange = (event, newValue) => {
    localStorage.setItem(ACTIVE_PROJECT_TAB, newValue);
    setValue(newValue);
  };

  const setProjectUsers = async (sharedProjectData) => {
    const userProjectData = sharedProjectData?.getSharedProjects?.map((users) => users.userId);

    const isFounded = userProjectData.some((userProjects) => userProjects.includes(authUser.id));

    if (userPermissions?.roleId === 3 && isFounded === false) { navigate('/'); return; }

    const projectUsers = sharedProjectData?.getSharedProjects?.filter((projectItem) =>
      parseInt(projectItem.workspaceProfileId, 10) !== workspaceProfile.id).map((row) =>
      ({
        id: row.id,
        name: `${row.workspaceProfile.displayName}`,
        userName: `${row.workspaceProfile.displayName}`,
        emailAddress: row.user.email,
        logo: row.workspaceProfile.logo,
        projectName: row.project.name,
        workspaceProfileId: row.workspaceProfile.id,
        projectCreatedBy: row.project.createdBy,
      }));

    await dispatch({ type: 'SET_USERS_PROJECTS_DETAIL', payload: { usersOfProjects: projectUsers } });
  };

  const currentVersionData = async () => {
    if (!isLoading) setIsLoading(true);
    try {
      await getCurrentVersionData({
        variables: {
          input: {
            projectId: parseInt(params.id, 10),
            showVersion: false,
          },
        },
      });
    } catch (error) {
      setIsLoading(false);
    }
  };

  const setUndoRedoEdits = async () => {
    const undoRedoData = await undoRedoEdits();
    // set undo redo details
    if (!undoRedoData.error && !undoRedoData.loading && undoRedoData.data) {
      setUndoEdits(!!undoRedoData?.data?.getUndoRedoVersion?.undo);
      setRedoEdits(!!undoRedoData?.data?.getUndoRedoVersion?.redo);
    }
  };

  const projectDetailData = async () => {
    if (!isLoading) setIsLoading(true);
    let activeTabIndex = localStorage.getItem(ACTIVE_PROJECT_TAB);
    if (!activeTabIndex || projectId !== params.id) { activeTabIndex = 0; }
    if (showTeamTour) { setValue(2); } else { setValue(parseInt(activeTabIndex, 10)); }

    try {
      const [projectResponse, projectWarningResponse] = await Promise.all([
        getProjectDetail(),
        getProjectWarnings(),
        setUndoRedoEdits(),
      ]);

      const { data: projectData, error: projectError, loading: projectLoading } = projectResponse;
      const { data: projectWarningData, error: projectWarningError, loading: projectWarningLoading } = projectWarningResponse;

      // get project details
      if (!projectError && !projectLoading && projectData && projectData.project) {
        setProject(projectData.project);
        if (projectData.project.isActive === false) navigate(`/${PROJECTS}`);
      } else {
        setProjectNotExists(true);
      }

      // get project warnings details
      if (!projectWarningError && !projectWarningLoading && projectWarningData) {
        await dispatchRiskBar({ type: 'GET_PROJECT_WARNINGS', payload: { projectWarnings: projectWarningData?.getProjectWarnings } });
      }
    } catch (error) {
      setIsLoading(false);
      navigate(`/${PROJECTS}`);
    }
  };

  useEffect(() => {
    const loadProjectVersionData = async () => {
      const [, shareProjectResponse, workspaceProfilesData] = await Promise.all([
        projectDetailData(),
        getSharedProject({ variables: { input: { workspaceId: workspace.id, projectId: parseInt(params.id, 10) } } }),
        getWorkspaceProfiles({ variables: { workspaceId: workspace.id } }),
      ]);

      // get shared users project details
      const { data: sharedProjectData, error: sharedProjectError, loading: sharedProjectLoading } = shareProjectResponse;
      if (!sharedProjectError && !sharedProjectLoading && sharedProjectData) {
        setProjectUsers(sharedProjectData);
      }

      // get workspace profiles
      const { data: workspaceProfilesList, error: workspaceProfilesError, loading: workspaceProfilesLoading } = workspaceProfilesData;
      if (!workspaceProfilesError && !workspaceProfilesLoading && workspaceProfilesList) {
        setWorkspaceProfiles(workspaceProfilesList.listWorkspaceProfiles.filter((profile) => !profile.isDisabled));
      }

      setReadOnlyMode(false);
      setIsCurrentVersion(true);
    };

    if (authUser && workspace && userPermissions && workspaceProfile) {
      if (!selectedListItem) loadProjectVersionData();
      else projectDetailData();
    }
  }, [selectedListItem, authUser, workspace, userPermissions, workspaceProfile]);

  useEffect(() => {
    dispatch({ type: 'SET_PROJECT_ID', payload: { projectId: params.id } });
  }, [value, project]);

  useEffect(() => {
    if (workspace && Object.keys(project).length) {
      if (value === 0) {
        document.title = `${workspace.name} | ${project.name} | Epics`;
      } else if (value === 1) {
        document.title = `${workspace.name} | ${project.name} | Timeline`;
      } else if (value === 2) {
        document.title = `${workspace.name} | ${project.name} | Team`;
      }
    }
  }, [value, project, workspace]);

  const onSuccess = async () => {
    const { data, loading, error } = await getSharedProject({ variables: { input: { workspaceId: workspace.id, projectId: parseInt(params.id, 10) } } });

    if (!error && !loading && data) {
      setProjectUsers(data);
    }
  };

  const restoreVersion = async () => {
    setOpenDialog(true);
  };

  const dialogHandleClickRestore = async () => {
    if (!isLoading) setIsLoading(true);
    setOpenDialog(false);
    const variables = { input: { id: selectedListItem?.id, projectId: parseInt(params.id, 10) } };
    await restorePreviousVersion({ variables });
    setShowVersionHistoryBar(false);
    setIsCurrentVersion(true);
    setSelectedListItem(null);
  };

  const dialogHandleClose = () => {
    setOpenDialog(false);
  };

  const handleUndo = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const variables = { input: { projectId: parseInt(params.id, 10) } };
      await undoVersionMutation({ variables });
      await projectDetailData();
      setUndo((prev) => !prev);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedo = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const variables = { input: { projectId: parseInt(params.id, 10) } };
      await redoVersionMutation({ variables });
      await projectDetailData();
      setRedo((prev) => !prev);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUndoKeyPress = (event) => {
    if (!isLoading && undoEdits && (event.metaKey || event.ctrlKey) && event.key === 'z') {
      handleUndo();
    }
  };

  const handleRedoKeyPress = (event) => {
    if (!isLoading && redoEdits && (event.metaKey || event.ctrlKey) && event.key === 'y') {
      event.preventDefault();
      handleRedo();
    }
  };

  const handleReadOnlyMode = async () => {
    setShowVersionHistoryBar(false);
    await currentVersionData();
    setReadOnlyMode(false);
    setIsCurrentVersion(true);
    setSelectedListItem(null);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleUndoKeyPress);
    document.addEventListener('keydown', handleRedoKeyPress);

    return () => {
      document.removeEventListener('keydown', handleUndoKeyPress);
      document.removeEventListener('keydown', handleRedoKeyPress);
    };
  }, [undoEdits, redoEdits]);

  return (
    <>
      {isHidden && (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '29px' }}>
          <Button icon primary outlinedColor style={{ marginLeft: '23px', height: '48px' }} onClick={handleReadOnlyMode}>
            <ArrowBackIcon />
          </Button>
          <div style={{ marginLeft: '25px' }}>
            <Typography className="version-history-name">
              {selectedListItem?.name
                ? selectedListItem.name
                : moment(selectedListItem?.createdAt, 'x').format('MMM DD, h:mm A')}
            </Typography>
            <Typography className="version-history-project-name">{project?.name}</Typography>
          </div>

          <Button primary style={{ display: selectedListItem?.currentVersion ? 'none' : '', marginLeft: '45px' }} onClick={restoreVersion}>
            Restore this version
          </Button>
        </div>
      )}
      {projectNotExists ? (<Error404 />) : (
        <div className="page-header" style={{ gap: 0, paddingTop: 25 }}>
          <div className="header-layout" style={{ minHeight: isHidden ? 'unset' : 48 }}>
            {!isHidden && <WorkspaceOptions />}
            <div style={{ display: 'flex', gap: 20 }}>
              <div className="share-project-avatar">
                <div className="avatar-wrap">
                  {!isHidden && (
                    <AvatarGroup max={8}>
                      {usersOfProjects &&
                        usersOfProjects.map((row) => (
                          <CustomTooltip key={row.id} title={`${row.userName}`} arrow>
                            {
                              row.logo
                                ? <Avatar src={row.logo} />
                                : <Avatar>{generateInitials(row.userName, 'P', 2)}</Avatar>
                            }
                          </CustomTooltip>
                        ))}
                    </AvatarGroup>
                  )}
                </div>
              </div>
              {
                !isHidden && (undoEdits || redoEdits) &&
                <div style={{ display: 'flex', gap: 10 }}>
                  {undoEdits && (
                    <div className="setting-btn">
                      <Button primary outlinedColor icon onClick={handleUndo}>
                        <img src={UndoButton} width={24} height={24} alt="undo" />
                      </Button>
                    </div>
                  )}
                  {redoEdits && (
                    <div className="setting-btn">
                      <Button primary outlinedColor icon onClick={handleRedo}>
                        <img src={RedoButton} width={24} height={24} alt="redo" />
                      </Button>
                    </div>
                  )}
                </div>
              }
              {!isHidden && (
                <>
                  {authUser && workspace && <div style={{ display: isHidden ? 'none' : '' }} className="share-project-btn"><ShareProjectButton project={project} profiles={workspaceProfiles} name="Share Project" onDone={onSuccess} sharedProjectData={usersOfProjects} /></div>}
                  <div style={{ display: isHidden ? 'none' : '' }} className="setting-btn"><SprintDialog projectId={params.id} /></div>
                  <div style={{ display: isHidden ? 'none' : '' }} className={showRiskBar ? 'alert-btn-dark' : 'alert-btn'}><RiskBar /></div>
                </>
              )}
              {userPermissions?.roleId !== 3 && <div className="three-dots-btn"><EditMenu /></div>}
            </div>
          </div>
          <div style={{ display: isHidden ? 'none' : '', marginTop: 10, marginBottom: 10 }}>
            <BreadCrumb projectName={project.name} />
          </div>
          {
            project.name
              ? <ProjectTitle name={project.name} />
              : <Skeleton animation="wave" variant="text" width={120} height={28} />
          }
        </div>
      )}
      {
        projectNotExists ? null : (
          <div className="project-detail-content">
            <Box sx={{ width: '100%' }}>
              <Box className="tab-outer">
                <Tabs
                  TabIndicatorProps={{ style: { background: '#2E72F6' } }}
                  style={{ marginLeft: '12px' }}
                  value={value}
                  onChange={onTabChange}
                  aria-label="basic tabs example"
                >
                  <Tab className="tab-item" label="Epics" {...a11yProps(0)} />
                  <Tab className="tab-item" label="Timeline" {...a11yProps(1)} />
                  <Tab className="tab-item" label="Team" {...a11yProps(2)} />
                </Tabs>
              </Box>
              <TabPanel value={value} index={0}>
                {project && project.id && project.name && (
                  <EpicList projectId={project.id} projectKey={project.key} />
                )}
              </TabPanel>
              <TabPanel value={value} index={1}>
                {project && project.id && project.name && (
                  <TimeLine
                    projectId={project.id}
                    projectKey={project.key}
                    projectName={project.name}
                  />
                )}
              </TabPanel>
              <TabPanel value={value} index={2}>
                {project && project.id && project.name && (
                  <MemberList profiles={workspaceProfiles} projectId={project.id} projectKey={project.key} />
                )}
              </TabPanel>
            </Box>
          </div>
        )
      }
      <span>
        {openDialog && (
          <Dialog
            className="custom-modal-dialog confirmation-modal-dialog"
            open={openDialog}
            onClose={dialogHandleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent className="dialog-content">
              <DialogContentText id="alert-dialog-description">
                {`Are you sure you want to restore ${selectedListItem?.name || moment(selectedListItem?.createdAt, 'x').format('MMM DD, h:mm A')}?`}
              </DialogContentText>
            </DialogContent>
            <DialogActions className="dialog-actions">
              <Button primary outlined onClick={dialogHandleClose} disableFocusRipple>Cancel</Button>
              <Button primary onClick={dialogHandleClickRestore} autoFocus disableFocusRipple>Restore</Button>
            </DialogActions>
          </Dialog>
        )}
      </span>
    </>
  );
};
export default ProjectDetail;
